/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { returnType } from '../ActionSet/ActionSetManager';
import { defaultValue } from './ActionUpdateRecord';

const ActionUpdateRecordManager: ActionManagerFn<'ActionUpdateRecord'> = (
  platform: Platform,
): ActionManager<'ActionUpdateRecord'> => {
  let callback = emptyFunction;
  let expressionCallback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionUpdateRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
        ],
      },
      targetAttribute: {
        label: 'Target Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
        attributeRefreshCallback: (cb) => {
          callback = cb;
        },
      },
      valueExpression: {
        label: 'Value Expression',
        type: 'expression',
        required: true,
        refreshCallback: (cb) => {
          expressionCallback = cb;
        },
        returnType: async (
          item: RequiredItem<'ActionUpdateRecord'>,
          { appUid, pageId, itemId },
        ) => {
          return returnType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.targetAttribute,
            itemType: item.type,
            platform,
            storeType: 'STORE',
          });
        },
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'alias') {
        callback();
        return {
          [property]: value,
          targetAttribute: defaultValue.targetAttribute,
          valueExpression: defaultValue.valueExpression,
        };
      }
      if (property === 'targetAttribute') {
        expressionCallback();
        return {
          [property]: value,
          valueExpression: defaultValue.valueExpression,
        };
      }
      return {
        [property]: value,
      };
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'targetAttribute':
          return !!props.alias;
        case 'valueExpression':
          return !!props.alias && !!props.targetAttribute;
        default:
          return true;
      }
    },
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    name: 'Update Record',
    getDisplayText: (item) =>
      item.props.alias
        ? `${item.props.targetAttribute ?? ''} (${item.props.alias})`
        : '',
    icon: { iconName: 'edit' },
    description:
      'Use this action to programmatically update the current record of a Data Store.',
  };
};

export default ActionUpdateRecordManager;
